import React, { useState, useEffect, useRef, useCallback } from "react";
import { fetchProjects } from "../api";
import "./MobileHomePage.css";

export default function MobileHomePage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [aboutMode, setAboutMode] = useState(false);

  // Overlay/fade state
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [overlayFading, setOverlayFading] = useState(false);
  const [overlayKey, setOverlayKey] = useState(0);
  const fadeTimeoutRef = useRef(null);

  // "Show" logic for only visible projects
  const visibleProjects = projects.filter(proj => proj.show);

  // For short project detection (for next project button)
  const [isShortProject, setIsShortProject] = useState(false);
  const projectContentRef = useRef(null);
  const scrollableRef = useRef(null);

  // --- Overlay Helper ---
  const triggerOverlayFade = useCallback(() => {
    setOverlayVisible(true);
    setOverlayFading(false);
    setOverlayKey((k) => k + 1);
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    // Fade overlay after 1100ms, then hide fully after 700ms more
    fadeTimeoutRef.current = setTimeout(() => {
      setOverlayFading(true);
      setTimeout(() => setOverlayVisible(false), 700);
    }, 1100);
  }, []);

  // --- Universal scroll reset (mobile friendly) ---
  const scrollToTop = useCallback(() => {
    if (scrollableRef.current) scrollableRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  // --- Fetch projects on mount, show overlay ---
  useEffect(() => {
    async function load() {
      const data = await fetchProjects();
      setProjects(data);
      triggerOverlayFade();
    }
    load();
    setOverlayVisible(true);
    setOverlayFading(false);
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = setTimeout(() => {
      setOverlayFading(true);
      setTimeout(() => setOverlayVisible(false), 700);
    }, 1100);
    // eslint-disable-next-line
  }, []);

  // --- Nav handler (Work/About/Project), with overlay and scroll reset ---
  const handleNav = useCallback(
    ({ toAbout = false, toProject = null }) => {
      const alreadyOnAbout = aboutMode && toAbout;
      const alreadyOnProject =
        selectedProject && toProject && selectedProject.id === toProject.id;
      const alreadyOnWork =
        !aboutMode && !selectedProject && !toAbout && !toProject;
      if (alreadyOnAbout || alreadyOnProject || alreadyOnWork) return;
      triggerOverlayFade();
      setTimeout(() => {
        setAboutMode(toAbout);
        setSelectedProject(toProject);
        scrollToTop();
      }, 24); // just after overlay appears
    },
    [aboutMode, selectedProject, triggerOverlayFade, scrollToTop]
  );

  // --- Short project detection (for sticky NEXT PROJECT button) ---
  useEffect(() => {
    if (!selectedProject) return setIsShortProject(false);
    const check = () => {
      if (!projectContentRef.current) return;
      const contentHeight = projectContentRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      setIsShortProject(contentHeight + 40 < windowHeight); // fudge factor
    };
    setTimeout(check, 100);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [selectedProject, projects]);

  // --- Full media renderer (iframe/video/img) ---
  const renderVideo = (url, key) => {
    if (!url) return null;
    const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
    if (isYouTube) {
      const videoId = url.includes("youtu.be/")
        ? url.split("youtu.be/")[1].split("?")[0]
        : new URL(url).searchParams.get("v");
      return (
        <div className="project-video-wrapper" key={`yt-${key}`}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title="YouTube video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    // native video
    return (
      <div className="project-video-wrapper" key={`vid-${key}`}>
        <video
          src={url}
          className="project-video"
          controls
          playsInline
          muted
        />
      </div>
    );
  };

  // --- Loader fallback on first load ---
  if (!projects.length) {
    return (
      <div className="mobile-loading">
        <div className="spinner" />
      </div>
    );
  }

  // --- Render ---
  return (
    <div className="mobile-homepage">
      <div className="mobile-nav">
        <div onClick={() => handleNav({ toAbout: false, toProject: null })}>Work</div>
        <a
          href="mailto:dubois.leo@hotmail.fr"
          onClick={e => {
            e.preventDefault();
            handleNav({ toAbout: false, toProject: null });
            window.location.href = "mailto:dubois.leo@hotmail.fr";
          }}
        >
          Contact
        </a>
        <div onClick={() => handleNav({ toAbout: true, toProject: null })}>About</div>
      </div>

      <div className="mobile-scrollable" ref={scrollableRef}>
        {aboutMode ? (
          <div className="mobile-about">
            <p>Imagery, AI, and design. Based in Paris.</p>
          </div>
        ) : selectedProject ? (
          <div className="mobile-project-content" ref={projectContentRef}>
            <h2 className="mobile-project-title">{selectedProject.title}</h2>
            <p className="mobile-project-description">
              {selectedProject.description}
            </p>
            {selectedProject.credits && (
              <p className="mobile-project-credits">
                {selectedProject.credits}
              </p>
            )}

            {/* Video1/Video2 if present (YouTube OR native video) */}
            {selectedProject.video1?.url && renderVideo(selectedProject.video1.url, 1)}
            {selectedProject.video2?.url && renderVideo(selectedProject.video2.url, 2)}

            {/* Gallery: can be img or mp4 */}
            {selectedProject.gallery.map((item) => {
              const isYouTube = item.url.includes("youtube.com") || item.url.includes("youtu.be");
              const isVideo = item.url.toLowerCase().endsWith(".mp4");
              return (
                <div key={item.id} className="mobile-image-wrapper">
                  {isYouTube
                    ? renderVideo(item.url, item.id)
                    : isVideo
                    ? (
                        <video
                          src={item.url}
                          className="mobile-image"
                          controls
                          playsInline
                          muted
                        />
                      )
                    : (
                        <img
                          src={item.url}
                          alt=""
                          className="mobile-image"
                        />
                      )
                  }
                </div>
              );
            })}

            {/* NEXT PROJECT */}
            <div
              className={
                "mobile-next-project" +
                (isShortProject ? " mobile-next-project-absolute" : "")
              }
              onClick={() => {
                const idx = visibleProjects.findIndex(
                  (p) => p.id === selectedProject.id
                );
                const nextIdx = (idx + 1) % visibleProjects.length;
                handleNav({ toAbout: false, toProject: visibleProjects[nextIdx] });
              }}
            >
              NEXT PROJECT
            </div>
          </div>
        ) : (
          <div className="mobile-project-grid">
            {visibleProjects.map((proj) => (
              <div
                key={proj.id}
                className="mobile-project-card"
                onClick={() => handleNav({ toAbout: false, toProject: proj })}
              >
                <img
                  src={proj.thumbnail?.url || proj.gallery[0]?.url}
                  alt=""
                  className="mobile-thumbnail"
                />
                <div className="mobile-project-title">{proj.title}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {overlayVisible && (
        <div
          key={overlayKey}
          className={`fade-overlay${overlayFading ? " fading" : ""}`}
        />
      )}
    </div>
  );
}
